import { useIntl } from 'react-intl'

import {
  IMAGE_MIME_TYPE,
  IMAGE_OR_PDF_MIME_TYPE,
  ONE_MB_IN_BYTES,
} from '@/utils/config'

import { useLegacyAuthApi } from '@/services/api/ApiContext'

const ACCIDENT_PHOTO_MAX_UPLOAD_FILE_SIZE = 30 * ONE_MB_IN_BYTES

export const usePhotoUpload = () => {
  const api = useLegacyAuthApi()
  const { formatMessage } = useIntl()

  const uploadPhoto = async ({
    url,
    file,
    formData,
    onSuccess,
    onError,
    allowAttachments = false,
    isLoginWithToken = false,
  }) => {
    if (!file || file.size === 0) {
      onError(
        formatMessage(
          { id: 'uploading.error.file_empty' },
          { fileName: file.name },
        ),
      )
      return false
    }
    if (file.size > ACCIDENT_PHOTO_MAX_UPLOAD_FILE_SIZE) {
      onError(
        formatMessage(
          { id: 'uploading.error.max_file_size' },
          { fileName: file.name },
          { maxSize: ACCIDENT_PHOTO_MAX_UPLOAD_FILE_SIZE },
        ),
      )
      return false
    }
    if (
      (allowAttachments && !file.type.match(IMAGE_OR_PDF_MIME_TYPE)) ||
      (!allowAttachments && !file.type.match(IMAGE_MIME_TYPE))
    ) {
      onError(
        formatMessage(
          {
            id: allowAttachments
              ? 'uploading.error.wrong_attachment_type'
              : 'uploading.error.wrong_file_type',
          },
          { fileName: file.name },
        ),
      )
      return false
    }

    await api
      .post(url, formData, {
        isLoginWithToken,
        headers: {
          'Content-Type': 'multipart/form-data',
          lang: document.lang || 'en',
        },
      })
      .then((response) => {
        onSuccess?.(response?.data)
      })
      .catch((error) => {
        onError?.(
          formatMessage(
            { id: 'uploading.error.file_error' },
            {
              fileName: file.name,
              errorText: error?.request?.responseText?.Description,
            },
          ),
        )
      })
    return true
  }
  const deletePhoto = async ({
    url,
    onSuccess,
    onError,
    isLoginWithToken = false,
  }) => {
    await api
      .delete(url, { isLoginWithToken })
      .then((response) => onSuccess?.(response?.data))
      .catch((error) => onError?.(error))
  }
  const updatePhoto = async ({
    url,
    data,
    onSuccess,
    onError,
    isLoginWithToken = false,
  }) => {
    await api
      .post(url, data, { isLoginWithToken })
      .then((response) => {
        onSuccess?.(response)
      })
      .catch((error) => {
        onError?.(error)
      })
  }
  const uploadPhotoSVG = async ({
    url,
    formData,
    onSuccess,
    onError,
    isLoginWithToken = false,
  }) => {
    await api
      .post(url, formData, {
        isLoginWithToken,
        headers: {
          'Content-Type': 'multipart/form-data',
          lang: document.lang || 'en',
        },
      })
      .then((response) => {
        onSuccess?.(response?.data)
      })
      .catch((error) => {
        if (onError)
          onError(`Error upload SVG, status:${error?.request?.responseText}`)
      })
    return true
  }

  const deletePhotoSVG = async ({
    url,
    onSuccess,
    onError,
    isLoginWithToken = false,
  }) => {
    await api
      .delete(url, { isLoginWithToken })
      .then((response) => onSuccess?.(response?.data))
      .catch((error) => onError?.(error))
  }
  return {
    uploadPhoto,
    deletePhoto,
    updatePhoto,
    uploadPhotoSVG,
    deletePhotoSVG,
  }
}
